<template>
  <div class="drawer-adjust-values">
    <div class="content-body">
      
      <div class="top-section">
        <p class="description">
          Ajuste os valores de todos os presentes de uma só vez, aplicando uma porcentagem de aumento ou redução.
        </p>

        <WarningRulesBanner 
          text="Aumentar o valor da sua lista pode trazer mais flexibilidade para realizar seus planos após o casamento."
          type="info"
          padding="16px"
          class="custom-info-banner"
        />

        <hr class="divider-bottom" />

        <div class="adjustment-summary">
          <div class="icon-wrapper">
            <base-icon 
              name="painel/gift-pink" 
              width="110" 
              height="113" 
              class="gift-icon"
            />
          </div>

          <p class="summary-text">
            O ajuste será aplicado a todos os <br />
            <span class="highlight-pink">{{ totalGifts }} presentes da sua lista.</span>
          </p>

          <div class="value-block">
            <p class="value-label">Valor original:<br />
            {{ formattedOriginalValue }}</p>
          </div>

          <div class="value-block estimated">
            <p class="value-label">Valor estimado:<br />
            {{ formattedEstimatedValue }}</p>
          </div>
        </div>

        <div v-if="limitMessage" class="custom-warning-banner">
          <base-icon name="painel/warning-outline-icon" width="20" height="20" class="warning-icon" />
          <span>{{ limitMessage }}</span>
        </div>
      </div>

      <div class="bottom-section">
        <hr class="divider-bottom" />
        <div class="percentage-control">
          <p class="control-label">Defina a porcentagem de aumento ou redução</p>
          
          <div class="control-row">
            <button 
              type="button" 
              class="btn-adjust btn-minus" 
              :disabled="!canApplyNextDiscount"
              @click="updatePercentage(-10)"
            >
              -10%
            </button>

            <div class="percentage-display">
              {{ adjustmentPercentage }}%
            </div>

            <button 
              type="button" 
              class="btn-adjust btn-plus" 
              @click="updatePercentage(10)"
            >
              +10%
            </button>
          </div>
        </div>
      </div>

    </div>

    <div class="footer">
      <button
        :disabled="adjustmentPercentage === 0"
        class="button is-primary is-fullwidth btn-apply"
        @click="applyAdjustment"
      >
        Aplicar
      </button>
    </div>
  </div>
</template>

<script>
import WarningRulesBanner from '@/components/Shared/WarningRulesBanner.vue'

export default {
  name: 'DrawerAdjustListValues',
  components: {
    WarningRulesBanner,
  },
  props: {
    data: {
      type: Object,
      default: () => ({ gifts: [], limits: null }),
      required: false,
    },
  },
  data() {
    return {
      adjustmentPercentage: 0,
    }
  },
  computed: {
    totalGifts() {
      return this.data.gifts?.length || 0
    },
    
    limits() {
      return this.data.limits || {
        lowest_gift_value: null,
        max_discount_percentage: 50,
        total_amount_with_max_discount: null,
        min_gift_value: 1.00
      }
    },
    
    minGiftValue() {
      return this.limits.min_gift_value || 1.00
    },
    
    maxDiscountPercentage() {
      return this.limits.max_discount_percentage || 50
    },
    
    totalAmountWithMaxDiscount() {
      if (!this.limits.total_amount_with_max_discount) return null
      
      const value = this.limits.total_amount_with_max_discount
        .replace(/\./g, '') 
        .replace(',', '.') 
      
      return parseFloat(value)
    },
    
    originalValueSum() {
      return this.data.gifts?.reduce((acc, gift) => {
        return acc + parseFloat(gift.valor_final || 0)
      }, 0) || 0
    },
    
    formattedOriginalValue() {
      return this.originalValueSum.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    
    estimatedTotalRaw() {
      return this.originalValueSum * (1 + (this.adjustmentPercentage / 100));
    },
    
    formattedEstimatedValue() {
      if (this.adjustmentPercentage === 0) {
        return '-';
      }
      
      return this.estimatedTotalRaw.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    },
    
    totalDiscountApplied() {
      return Math.abs(Math.min(0, this.adjustmentPercentage))
    },
    
    limitMessage() {
      if (!this.data.gifts || this.adjustmentPercentage > 0 || this.canApplyNextDiscount) {
        return null;
      }
      
      return "Um presente atingiu o valor mínimo permitido.";
    },
    
    canApplyNextDiscount() {
      if (!this.data.gifts) return false
      
      const nextPercentage = this.adjustmentPercentage - 10
      
      const wouldViolateMinGiftValue = this.data.gifts.some(gift => {
        const currentValue = parseFloat(gift.valor_final || 0)
        const nextValue = currentValue * (1 + (nextPercentage / 100))
        return nextValue < this.minGiftValue
      })
      
      if (wouldViolateMinGiftValue) return false
      
      const nextDiscountTotal = Math.abs(Math.min(0, nextPercentage))
      if (nextDiscountTotal > this.maxDiscountPercentage) return false
      
      if (this.totalAmountWithMaxDiscount !== null) {
        const nextTotal = this.originalValueSum * (1 + (nextPercentage / 100))
        if (nextTotal < this.totalAmountWithMaxDiscount) return false
      }
      
      return true
    }
  },
  methods: {
    parseBackendValue(value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      
      return parseFloat(
        value
          .replace(/\./g, '') 
          .replace(',', '.') 
      )
    },
    
    updatePercentage(val) {
      if (val < 0 && !this.canApplyNextDiscount) {
        return
      }
      
      this.adjustmentPercentage += val
    },
    
    applyAdjustment() {
      this.$emit('apply', { percentage: this.adjustmentPercentage })
    }
  }
}
</script>

<style lang="scss" scoped>
.drawer-adjust-values {
  $text-color: #5d758d;
  $divider-color: #dde7f1;
  $banner-bg: #f2fbfa;
  $banner-main: #3dafb0;
  $pink-highlight: #E583A2; 
  $btn-bg-minus: #f0f2f5;
  $btn-bg-plus: #f2fbfa;
  $warning-text: #E29400;
  $warning-border: #FFDF1B;
  $warning-bg: #FFFFDB;

  min-height: calc(100vh - 66px);
  display: flex;
  flex-direction: column;

  .content-body {
    flex: 1;
    padding: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
  }

  .bottom-section {
    margin-top: auto;
    width: 100%;
  }

  .description {
    font-size: 14px;
    line-height: 20px;
    color: $text-color;
    margin-bottom: 24px;
  }

  .custom-info-banner {
    margin-bottom: 20px;
    ::v-deep {
      background-color: $banner-bg !important;
      border: 1px solid $banner-main !important;
      border-radius: 12px;
      .message-text, p, span { color: $banner-main !important; text-align: left !important; }
      .icon, i, svg, path { color: $banner-main !important; fill: $banner-main !important; }
    }
  }

  .custom-warning-banner {
    margin-top: 10px;
    margin-bottom: 20px;
    background-color: $warning-bg;
    border: 1px solid $warning-border;
    border-radius: 12px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    text-align: left;

    span {
      color: $warning-text;
      font-size: 14px;
      font-weight: 500;
    }

    .warning-icon {
      color: $warning-text;
      fill: $warning-text;
      margin-right: 12px;
      flex-shrink: 0;
    }
  }

  .divider-bottom {
    height: 1px;
    background-color: $divider-color;
    border: 0;
    margin: 0 -16px 32px -16px;
  }

  .adjustment-summary {
    padding-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    .summary-text { 
      font-size: 18px; 
      color: $text-color; 
      line-height: 1.4; }
    .value-block {
      .value-label { 
        font-size: 18px; 
        font-weight: 600; 
        color: $text-color; }
      &.estimated {
        .value-label { color: $pink-highlight; }
      }
    }
    .highlight-pink { 
      color: $pink-highlight; 
      font-weight: 600; 
    }
  }

  .percentage-control {
    .control-label { 
      font-size: 16px; 
      color: $text-color; 
      margin-bottom: 16px; 
    }
    .control-row { 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      gap: 12px; 
    }
    .btn-adjust {
      width: 80px; 
      height: 60px; 
      border-radius: 12px; 
      border: 1px solid transparent;
      font-size: 18px; 
      font-weight: 600; 
      cursor: pointer;
      &:disabled { 
        opacity: 0.5; 
        cursor: not-allowed; 
      }
      &.btn-minus { 
        background-color: $btn-bg-minus; 
        color: $text-color; 
      }
      &.btn-plus { 
        background-color: $btn-bg-plus; 
        color: $banner-main; 
        border-color: $banner-main; 
      }
    }
    .percentage-display { 
      min-width: 140px; 
      color: $text-color; 
      font-size: 32px; 
      font-weight: 700; 
    }
  }

  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    background-color: white;
    border-top: 1px solid $divider-color;
    z-index: 10;
    .btn-apply { height: 48px; font-weight: bold; }
  }
}
</style>